import { useCallback, useState } from 'react'
import {
  corDoTextoSobre,
  ROTULO_TIPO_TAREFA,
  TIPO_TAREFA,
  type ConversaDto,
  type TipoTarefa,
} from '@crm/shared'
import { ErroRequisicao } from '../../api/cliente'
import { useEquipe, useEu } from '../../api/hooks'
import { useCriarTag, useDefinirTags, useTags } from '../../api/hooks-leads'
import { corDaNovaEtiqueta, etiquetaDeMesmoNome } from '../../componentes/etiqueta-nova'
import { useCriarTarefa } from '../../api/hooks-operacao'
import { useFecharAoClicarFora } from '../../componentes/fechar-fora'
import { Flutuante } from '../../componentes/Flutuante'
import { IconeEtiqueta } from '../../componentes/icones'
import { Aviso, Botao, Campo, Modal, juntar } from '../../componentes/ui'
import { pode } from '../../permissao'

/**
 * Ações sobre o contato feitas de dentro da conversa: etiquetar e agendar
 * tarefa.
 *
 * Ficam aqui, e não em `ExtrasDoChat`, porque aquele arquivo trata do que
 * acontece **com a mensagem** — resposta rápida, agendamento de envio. Estas
 * duas mexem no contato, não na conversa.
 */

/**
 * Agendar tarefa (5.3).
 *
 * Diferente de agendar mensagem: aqui nada é enviado ao cliente. É um
 * compromisso do vendedor — ligar, visitar, retomar — que nasce amarrado ao
 * contato, aparece no calendário e vira notificação na hora marcada, pelo
 * mesmo caminho de qualquer outra tarefa.
 */
export function ModalTarefa({
  aberto,
  conversa,
  aoFechar,
}: {
  aberto: boolean
  conversa: ConversaDto
  aoFechar: () => void
}) {
  const criar = useCriarTarefa()
  const { data: usuarios } = useEquipe()

  const [titulo, setTitulo] = useState('')
  const [tipo, setTipo] = useState<TipoTarefa>('ligacao')
  const [quando, setQuando] = useState('')
  const [responsavel, setResponsavel] = useState('')
  const [descricao, setDescricao] = useState('')
  const [lembrete, setLembrete] = useState(true)

  const erro = criar.error instanceof ErroRequisicao ? criar.error : null
  const tituloPadrao = `${ROTULO_TIPO_TAREFA[tipo]}: ${conversa.nome}`

  function salvar() {
    criar.mutate(
      {
        titulo: titulo || tituloPadrao,
        tipo,
        descricao: descricao || null,
        contatoId: conversa.contatoId,
        usuarioId: responsavel || null,
        // O `datetime-local` já dá a hora local; o ISO mantém o fuso certo.
        dataHora: new Date(quando).toISOString(),
        lembrete,
        som: false,
      },
      {
        onSuccess: () => {
          setTitulo('')
          setQuando('')
          setDescricao('')
          aoFechar()
        },
      },
    )
  }

  return (
    <Modal
      aberto={aberto}
      titulo={`Agendar tarefa sobre ${conversa.nome}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            carregando={criar.isPending}
            onClick={salvar}
            disabled={!quando}
          >
            Agendar
          </Botao>
        </>
      }
    >
      <div className="space-y-3">
        {erro ? <Aviso tipo="erro">{erro.corpo.mensagem}</Aviso> : null}

        <Aviso>
          Isto não envia nada ao cliente. Entra no calendário amarrado a este contato e vira
          notificação na hora marcada.
        </Aviso>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-tinta-600">Tipo</span>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoTarefa)}
              className="w-full rounded-md ring-1 ring-tinta-200 bg-superficie px-2 py-1.5 text-sm"
            >
              {TIPO_TAREFA.map((t) => (
                <option key={t} value={t}>
                  {ROTULO_TIPO_TAREFA[t]}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-tinta-600">Quando</span>
            <input
              type="datetime-local"
              value={quando}
              onChange={(e) => setQuando(e.target.value)}
              className="w-full rounded-md ring-1 ring-tinta-200 bg-superficie px-2 py-1.5 text-sm"
            />
          </label>
        </div>

        <Campo
          rotulo="Título"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder={tituloPadrao}
          dica="Em branco, usa o tipo e o nome do contato."
          erro={erro?.campo('titulo')}
        />

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-tinta-600">Para quem</span>
          <select
            value={responsavel}
            onChange={(e) => setResponsavel(e.target.value)}
            className="w-full rounded-md ring-1 ring-tinta-200 bg-superficie px-2 py-1.5 text-sm"
          >
            <option value="">Para mim</option>
            {(usuarios ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-tinta-600">Observação</span>
          <textarea
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            rows={3}
            className="w-full rounded-md ring-1 ring-tinta-200 bg-superficie px-2 py-1.5 text-sm"
          />
        </label>

        <label className="flex cursor-pointer items-center gap-2 text-sm text-tinta-600">
          <input
            type="checkbox"
            checked={lembrete}
            onChange={(e) => setLembrete(e.target.checked)}
            className="h-3.5 w-3.5 accent-marca"
          />
          Avisar por notificação na hora marcada
        </label>
      </div>
    </Modal>
  )
}

/**
 * Etiquetas do contato, sem sair da conversa (4.6).
 *
 * A rota substitui o conjunto inteiro, então é isso que se manda a cada
 * mudança. Mandar uma etiqueta por vez abriria espaço para dois cliques
 * rápidos se atropelarem e uma delas sumir.
 */
/**
 * As etiquetas do contato, na ficha lateral.
 *
 * Foi um botão de ícone na barra do chat até ter dois desenhos ao mesmo
 * tempo — ícone lá, campo aqui. Ficou só o campo: etiquetar é coisa que se
 * faz olhando a ficha, junto do resto do que se sabe do cliente, e manter
 * dois caminhos para a mesma coisa é manter dois caminhos para consertar.
 */
export function SeletorEtiquetas({ conversa }: { conversa: ConversaDto }) {
  const [aberto, setAberto] = useState(false)
  const [chaveDaBusca, setChaveDaBusca] = useState(0)
  const fechar = useCallback(() => {
    setAberto(false)
    // Limpa a busca ao fechar: reabrir filtrado pelo que se digitou da última
    // vez parece lista incompleta.
    setChaveDaBusca((c) => c + 1)
  }, [])
  const caixa = useFecharAoClicarFora<HTMLDivElement>(aberto, fechar)

  /*
    Na ficha lateral: um campo que abre a lista, com busca.

    A primeira versão empilhava todas as etiquetas como botões marcáveis. Com
    meia dúzia funcionava; com trinta, a seção sozinha ficava mais alta que o
    resto da ficha inteira e achar uma virava caça visual. Fechado, o campo
    mostra o que está marcado e ocupa uma linha.
  */
  return (
    <div ref={caixa} className="relative">
      <button
        type="button"
        onClick={() => setAberto((a) => !a)}
        className="flex w-full items-center gap-2 rounded-md ring-1 ring-tinta-200 bg-superficie px-2 py-1.5 text-left text-sm hover:ring-marca"
      >
        <IconeEtiqueta className="h-4 w-4 shrink-0 text-tinta-400" />

        <span className="flex min-w-0 flex-1 flex-wrap gap-1">
          {conversa.tags.length === 0 ? (
            <span className="text-tinta-400">Selecione</span>
          ) : (
            conversa.tags.map((t) => (
              <span
                key={t.id}
                className="rounded px-1.5 py-px text-[11px] font-medium"
                style={{ backgroundColor: t.cor, color: corDoTextoSobre(t.cor) }}
              >
                {t.nome}
              </span>
            ))
          )}
        </span>

        <span className="shrink-0 text-xs text-tinta-400">{aberto ? '▴' : '▾'}</span>
      </button>

      {/*
        Flutua fora da ficha, que rola por dentro: perto do pé dela a lista
        abria cortada, sem as últimas etiquetas.
      */}
      <Flutuante
        ancora={caixa}
        aberto={aberto}
        aoFechar={fechar}
        distancia={4}
        alturaMaxima={300}
        className="flex flex-col overflow-hidden rounded-xl bg-superficie shadow-media ring-1 ring-tinta-200"
      >
        <EscolhaDeEtiquetas key={chaveDaBusca} conversa={conversa} />
      </Flutuante>
    </div>
  )
}

/**
 * A janela de etiquetas, aberta pelo botão do cabeçalho da conversa.
 *
 * A mesma lista da ficha, num lugar que está sempre à vista: etiquetar é
 * coisa de toda conversa, e abrir a ficha só para isso era um passo a mais.
 * Quem pode mexer nas etiquetas também cria uma aqui, escrevendo o nome na
 * busca (ADR 0069); o "Criar" só aparece quando não há outra com o mesmo nome.
 */
export function JanelaDeEtiquetas({
  conversa,
  aoFechar,
}: {
  conversa: ConversaDto
  aoFechar: () => void
}) {
  return (
    <Modal
      aberto
      titulo={`Etiquetas de ${conversa.nome}`}
      aoFechar={aoFechar}
      rodape={
        <Botao variante="primario" onClick={aoFechar}>
          Pronto
        </Botao>
      }
    >
      <p className="mb-2 text-sm text-tinta-500">Cada clique marca ou desmarca, e já fica salvo.</p>
      <div className="rounded-md ring-1 ring-tinta-200">
        <EscolhaDeEtiquetas conversa={conversa} alta />
      </div>
    </Modal>
  )
}

/** A busca e a lista de etiquetas que se marcam com um clique. */
function EscolhaDeEtiquetas({ conversa, alta = false }: { conversa: ConversaDto; alta?: boolean }) {
  const { data: tags } = useTags()
  const { data: eu } = useEu()
  const definir = useDefinirTags()
  const criarTag = useCriarTag()
  const [procura, setProcura] = useState('')

  const marcadas = new Set(conversa.tags.map((t) => t.id))
  // `TagDto` não tem "ativo": a rota de etiquetas já devolve só as em uso.
  const disponiveis = tags ?? []
  const busca = procura.trim().toLowerCase()
  const filtradas = busca
    ? disponiveis.filter((t) => t.nome.toLowerCase().includes(busca))
    : disponiveis
  const falha = definir.error ?? criarTag.error
  const erro = falha instanceof ErroRequisicao ? falha.corpo.mensagem : null

  // Criar na hora (ADR 0069): só quem mexe nas etiquetas, e só nome que ainda não existe.
  const nomeNovo = procura.trim()
  const podeCriar = pode(eu, 'configuracao.etiquetas')
  const oferecerCriar = podeCriar && nomeNovo !== '' && !etiquetaDeMesmoNome(disponiveis, nomeNovo)

  function alternar(tagId: string) {
    const novo = marcadas.has(tagId)
      ? [...marcadas].filter((id) => id !== tagId)
      : [...marcadas, tagId]
    definir.mutate({ contatoId: conversa.contatoId, tagIds: novo })
  }

  function criarEMarcar() {
    if (!oferecerCriar || criarTag.isPending) return
    criarTag.mutate(
      { nome: nomeNovo, cor: corDaNovaEtiqueta(disponiveis.length) },
      {
        onSuccess: (criada) => {
          setProcura('')
          // A etiqueta nova já entra marcada: foi para isso que ela foi criada.
          definir.mutate({ contatoId: conversa.contatoId, tagIds: [...marcadas, criada.id] })
        },
      },
    )
  }

  return (
    <>
      {/*
        A busca aparece sempre.

        Cheguei a escondê-la abaixo de sete etiquetas, por achar que com
        poucas o campo era mais trabalho do que os olhos. Errado por dois
        motivos: a base cresce e o comportamento mudaria sozinho de um
        dia para o outro, e quem já digita para filtrar não quer descobrir
        que às vezes não dá.
      */}
      <div className="border-b border-tinta-100 p-2">
        <input
          value={procura}
          onChange={(e) => setProcura(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            // Enter marca a única que sobrou na busca, ou cria a que não existe.
            if (filtradas.length === 1 && filtradas[0]) alternar(filtradas[0].id)
            else criarEMarcar()
          }}
          placeholder={podeCriar ? 'Buscar ou criar etiqueta' : 'Buscar etiqueta'}
          autoFocus
          className="w-full rounded ring-1 ring-tinta-200 px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-marca/20"
        />
      </div>

      <ul className={juntar('min-h-0 overflow-y-auto py-1', alta ? 'max-h-80' : 'max-h-56')}>
        {oferecerCriar ? (
          <li>
            <button
              type="button"
              onClick={criarEMarcar}
              disabled={criarTag.isPending}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm font-medium text-marca hover:bg-marca/5 disabled:opacity-60"
            >
              <span aria-hidden className="w-4 shrink-0 text-center">+</span>
              <span className="truncate">
                {criarTag.isPending ? 'Criando…' : `Criar etiqueta “${nomeNovo}”`}
              </span>
            </button>
          </li>
        ) : null}
        {filtradas.length === 0 ? (
          oferecerCriar ? null : (
            <li className="px-3 py-4 text-center text-xs text-tinta-400">
              {disponiveis.length === 0
                ? podeCriar
                  ? 'Nenhuma etiqueta ainda. Escreva o nome acima para criar a primeira.'
                  : 'Nenhuma etiqueta criada ainda.'
                : 'Nenhuma com esse nome.'}
            </li>
          )
        ) : (
          filtradas.map((tag) => {
            const marcada = marcadas.has(tag.id)
            return (
              <li key={tag.id}>
                <button
                  type="button"
                  onClick={() => alternar(tag.id)}
                  aria-pressed={marcada}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-tinta-50"
                >
                  <input type="checkbox" checked={marcada} readOnly className="shrink-0" />
                  <span
                    className="h-3 w-3 shrink-0 rounded-full"
                    style={{ backgroundColor: tag.cor }}
                  />
                  <span className="truncate">{tag.nome}</span>
                </button>
              </li>
            )
          })
        )}
      </ul>
      {erro ? <p className="px-3 pb-2 text-xs text-red-700">{erro}</p> : null}
    </>
  )
}
